import {
  type MutateOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import { createOrganizationInvitesQueryKey } from '@/utils/queryKeys';
import type {
  InviteOrganizationMembersMutation,
  InviteOrganizationMembersMutationVariables,
} from './useInviteOrganizationMembers.generated';

const query = gql`
  mutation InviteOrganizationMembers($input: InviteOrganizationMembersInput!) {
    inviteOrganizationMembers(input: $input)
  }
`;

export const useInviteOrganizationMembers = (organizationId: string) => {
  const queryClient = useQueryClient();

  const { mutate, isPending, reset, error } = useMutation<
    InviteOrganizationMembersMutation,
    QueryError,
    InviteOrganizationMembersMutationVariables
  >({
    mutationFn: (variables) => {
      return gqlClient.request<
        InviteOrganizationMembersMutation,
        InviteOrganizationMembersMutationVariables
      >(query, variables);
    },
    onSuccess: () => {
      // Inviting creates pending invites, not members — refresh the invites
      // list (which also drives the seat count).
      queryClient.invalidateQueries({
        queryKey: createOrganizationInvitesQueryKey(organizationId),
      });
    },
  });

  const inviteMembers = (
    values: Omit<
      InviteOrganizationMembersMutationVariables['input'],
      'organizationId'
    >,
    options?: MutateOptions<
      InviteOrganizationMembersMutation,
      QueryError,
      InviteOrganizationMembersMutationVariables
    >
  ) => {
    return mutate({ input: { ...values, organizationId } }, options);
  };

  return {
    inviteMembers,
    isPending,
    reset,
    error,
  };
};
